import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Send, Paperclip, X } from 'lucide-react';
import { useRef } from 'react';

interface MessageComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onFileSelect: (file: File) => void;
  disabled: boolean;
  hasChannel: boolean;
  selectedFile?: File | null;
  onClearFile?: () => void;
}

export function MessageComposer({ 
  value, 
  onChange, 
  onSend, 
  onFileSelect,
  disabled, 
  hasChannel,
  selectedFile,
  onClearFile 
}: MessageComposerProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Nothing to send to until a chat is picked, so the whole row is inert, Send included.
  const inactive = disabled || !hasChannel;

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileSelect(file);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="p-4">
      {selectedFile && (
        <div className="mb-2 flex items-center gap-2 p-2 bg-secondary rounded-md">
          <Paperclip className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm flex-1 truncate">{selectedFile.name}</span>
          <span className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFile}
            className="h-6 w-6 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      <div className="flex gap-2">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
          disabled={inactive}
        />
        <Button
          variant="outline"
          size="icon"
          onClick={handleFileClick}
          aria-label="Attach a file"
          disabled={inactive}
        >
          <Paperclip className="h-4 w-4" />
        </Button>
        <Input
          placeholder={hasChannel ? 'Type a message' : 'Pick a chat to start writing'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          disabled={inactive}
        />
        <Button onClick={onSend} disabled={inactive}>
          <Send className="h-4 w-4 mr-1" />
          Send
        </Button>
      </div>
    </div>
  );
}
